import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  decodeAuthToken,
  MeMembership,
  TokenMembership,
} from "@/integrations/api/client/client";
import { connectCaseEventsHub, disconnectCaseEventsHub } from "@/services/realtime";

export interface UserRole {
  id: number;
  nameAr: string;
  nameEn: string;
}

export type FrontendUserRole = "admin" | "doctor" | "nurse";

export type Membership = TokenMembership;

type RoleItem = string | UserRole;

export interface User {
  id: string;
  nameAr: string;
  nameEn: string;
  email: string;
  phone: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  lastLoginAt: string;
  lastActivityAt: string;
  createdAt: string;
  roles: UserRole[];
}

interface ApiUserData {
  id?: string;
  nameAr?: string;
  nameEn?: string;
  name?: string;
  email?: string;
  phone?: string;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  lastLoginAt?: string;
  lastActivityAt?: string;
  createdAt?: string;
  roles?: RoleItem[];
  memberships?: MeMembership[];
  isPlatformAdmin?: boolean;
}

function meMembershipToTokenMembership(m: MeMembership): TokenMembership {
  return {
    tenantId: m.tenantId,
    roleId: m.roleId,
    role: m.roleName,
    isPrimary: m.isPrimary,
  };
}

interface AuthContextType {
  user: User | null;
  session: { user: User } | null;
  userRole: FrontendUserRole | null;
  memberships: Membership[];
  isPlatformAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (
    name: string,
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Normalize roles from backend/user storage:
 * - ["Admin"] => ["Admin"]
 * - [{ id: 1, nameEn: "Admin", nameAr: "مدير" }] => ["Admin"]
 */
export function normalizeRoles(roles: RoleItem[] | undefined): string[] {
  if (!roles) return [];
  return roles.map((r) => (typeof r === "string" ? r : (r?.nameEn ?? ""))).filter(Boolean);
}

function toUserRole(r: RoleItem): UserRole {
  if (typeof r === "string") return { id: 0, nameAr: r, nameEn: r };
  return { id: r.id ?? 0, nameAr: r.nameAr ?? "", nameEn: r.nameEn ?? "" };
}

function apiUserToUser(d: ApiUserData, fallback?: Partial<User>): User {
  const roles = (d.roles ?? fallback?.roles ?? []).map(toUserRole);
  const legacyName = d.name ?? "";
  return {
    id: d.id ?? fallback?.id ?? "",
    nameAr: d.nameAr ?? fallback?.nameAr ?? legacyName,
    nameEn: d.nameEn ?? fallback?.nameEn ?? legacyName,
    email: d.email ?? fallback?.email ?? "",
    phone: d.phone ?? fallback?.phone ?? "",
    isEmailVerified: d.isEmailVerified ?? fallback?.isEmailVerified ?? false,
    isPhoneVerified: d.isPhoneVerified ?? fallback?.isPhoneVerified ?? false,
    lastLoginAt: d.lastLoginAt ?? fallback?.lastLoginAt ?? "",
    lastActivityAt: d.lastActivityAt ?? fallback?.lastActivityAt ?? "",
    createdAt: d.createdAt ?? fallback?.createdAt ?? "",
    roles,
  };
}

// Map backend roles to frontend roles. `isPlatformAdmin` is intentionally
// ignored here — the platform-admin surface lives in a separate app
// (superadmin-saleem), so platform admins fall back to their tenant role.
function mapBackendRoleToFrontend(
  roles: RoleItem[] | undefined
): FrontendUserRole {
  const roleNames = normalizeRoles(roles).map((r) => r.toLowerCase());
  if (roleNames.includes("admin")) return "admin";
  if (roleNames.includes("doctor")) return "doctor";
  if (roleNames.includes("nurse")) return "nurse";
  return ((roleNames[0] as FrontendUserRole) || "nurse") as FrontendUserRole;
}

function readDecodedFromToken(): {
  memberships: Membership[];
  isPlatformAdmin: boolean;
} {
  const token = apiClient.getToken();
  if (!token) return { memberships: [], isPlatformAdmin: false };
  const decoded = decodeAuthToken(token);
  if (!decoded) return { memberships: [], isPlatformAdmin: false };
  return { memberships: decoded.memberships, isPlatformAdmin: decoded.isPlatformAdmin };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<{ user: User } | null>(null);
  const [userRole, setUserRole] = useState<FrontendUserRole | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // SignalR `case-events` hub lifecycle — start when the user authenticates,
  // tear down on logout/unmount. On `case.created` / `case.statusChanged`
  // the hub invalidates ["case-reviews"] and ["patient-queue"].
  useEffect(() => {
    if (!user) {
      disconnectCaseEventsHub();
      return;
    }
    connectCaseEventsHub(queryClient);
    return () => {
      disconnectCaseEventsHub();
    };
  }, [user, queryClient]);

  const applyTokenClaims = useCallback(() => {
    const { memberships: m, isPlatformAdmin: p } = readDecodedFromToken();
    setMemberships(m);
    setIsPlatformAdmin(p);
    return { memberships: m, isPlatformAdmin: p };
  }, []);

  // 30 minute inactivity timeout for medical compliance
  const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

  const signOutOnInactivity = useCallback(() => {
    apiClient.logout();
    localStorage.removeItem("auth_user");
    setUser(null);
    setSession(null);
    setUserRole(null);
    setMemberships([]);
    setIsPlatformAdmin(false);
    navigate("/auth");
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(signOutOnInactivity, INACTIVITY_TIMEOUT_MS);
    };
    const events: (keyof WindowEventMap)[] = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [user, signOutOnInactivity, INACTIVITY_TIMEOUT_MS]);

  useEffect(() => {
    const init = async () => {
      try {
        // 1) Restore from localStorage first
        const raw = localStorage.getItem("auth_user");
        const storedData: ApiUserData | null = raw ? JSON.parse(raw) : null;

        const token = apiClient.getToken();

        if (storedData && token) {
          const safeStoredUser = apiUserToUser(storedData);
          applyTokenClaims();

          setUser(safeStoredUser);
          setSession({ user: safeStoredUser });
          setUserRole(mapBackendRoleToFrontend(safeStoredUser.roles));

          const response = await apiClient.getCurrentUser();

          // /me failed for a non-auth reason (the client auto-logs out on a real 401
          // and redirects to /auth before returning here). Keep the locally-restored
          // session instead of bouncing the user back to login on every refresh.
          if (response?.error && !apiClient.getToken()) {
            setUser(null);
            setSession(null);
            setUserRole(null);
            setMemberships([]);
            setIsPlatformAdmin(false);
            return;
          }

          if (response?.data) {
            const apiData = response.data as ApiUserData;
            const updatedUser = apiUserToUser(apiData, safeStoredUser);
            localStorage.setItem("auth_user", JSON.stringify(updatedUser));
            setUser(updatedUser);
            setSession({ user: updatedUser });
            setUserRole(mapBackendRoleToFrontend(updatedUser.roles));
            // /me is the authoritative source for memberships once it returns —
            // override anything we inferred from the (possibly stale) JWT claim.
            if (apiData.memberships) {
              setMemberships(apiData.memberships.map(meMembershipToTokenMembership));
            }
            if (typeof apiData.isPlatformAdmin === "boolean") {
              setIsPlatformAdmin(apiData.isPlatformAdmin);
            }
          }
        }
      } catch {
        // If something goes wrong, logout to avoid broken state
        apiClient.logout();
        setUser(null);
        setSession(null);
        setUserRole(null);
        setMemberships([]);
        setIsPlatformAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [applyTokenClaims]);

  const signIn = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.login(email, password);

      if (response?.error) {
        return {
          success: false,
          error: response.error.message || "Invalid email or password",
        };
      }

      if (response?.data) {
        applyTokenClaims();
        // Fetch current user info to get accurate roles from backend
        try {
          const userResponse = await apiClient.getCurrentUser();

          if (userResponse?.data) {
            const userData = userResponse.data as ApiUserData;
            const userWithRoles = apiUserToUser(userData);

            localStorage.setItem("auth_user", JSON.stringify(userWithRoles));

            setUser(userWithRoles);
            setSession({ user: userWithRoles });
            setUserRole(mapBackendRoleToFrontend(userWithRoles.roles));
            if (userData.memberships) {
              setMemberships(userData.memberships.map(meMembershipToTokenMembership));
            }
            if (typeof userData.isPlatformAdmin === "boolean") {
              setIsPlatformAdmin(userData.isPlatformAdmin);
            }

            return { success: true };
          }

          const tokenUser = apiClient.getUser() as ApiUserData | null;
          if (tokenUser) {
            const safeUser = apiUserToUser(tokenUser);

            localStorage.setItem("auth_user", JSON.stringify(safeUser));

            setUser(safeUser);
            setSession({ user: safeUser });
            setUserRole(mapBackendRoleToFrontend(safeUser.roles));
            return { success: true };
          }

          return { success: false, error: "Unable to load user profile" };
        } catch {
          const tokenUser = apiClient.getUser() as ApiUserData | null;
          if (tokenUser) {
            const safeUser = apiUserToUser(tokenUser);

            localStorage.setItem("auth_user", JSON.stringify(safeUser));

            setUser(safeUser);
            setSession({ user: safeUser });
            setUserRole(mapBackendRoleToFrontend(safeUser.roles));
            return { success: true };
          }

          return { success: false, error: "Unable to load user profile" };
        }
      }

      return { success: false, error: "Unexpected response from server" };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred during sign in";
      return { success: false, error: message };
    }
  };

  const signUp = async (
    name: string,
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.register(name, email, password);

      if (response?.error) {
        return {
          success: false,
          error: response.error.message || "Registration failed",
        };
      }

      if (response?.data) {
        applyTokenClaims();
        const userData = apiClient.getUser() as ApiUserData | null;
        if (userData) {
          const safeUser = apiUserToUser(userData);

          localStorage.setItem("auth_user", JSON.stringify(safeUser));

          setUser(safeUser);
          setSession({ user: safeUser });
          setUserRole(mapBackendRoleToFrontend(safeUser.roles));
        }
        return { success: true };
      }

      return { success: false, error: "Unexpected response from server" };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "An error occurred during sign up";
      return { success: false, error: message };
    }
  };

  const signOut = async () => {
    apiClient.logout();
    localStorage.removeItem("auth_user");
    setUser(null);
    setSession(null);
    setUserRole(null);
    setMemberships([]);
    setIsPlatformAdmin(false);
    navigate("/auth");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRole,
        memberships,
        isPlatformAdmin,
        loading,
        signOut,
        signIn,
        signUp,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
